<template>
	<section class="run-summary panel">
		<div class="run-overview">
			<span v-if="running" class="provider-status-icon running" />
			<span v-else class="status-icon" :class="{ failed }">
				<AppIcon :name="failed ? IconName.Close : IconName.Check" :size="16" />
			</span>
			<div class="run-copy">
				<strong>{{ title }}</strong>
				<p>{{ subjectDetails }}</p>
			</div>
		</div>
		<div class="run-metrics">
			<div>
				<strong>{{ duration }}</strong>
				<span>{{ t("contactResearchPage.run.elapsed") }}</span>
			</div>
			<div>
				<strong>{{ completedProviderCount }} / {{ providerCount }}</strong>
				<span>{{ t("contactResearchPage.run.sources") }}</span>
			</div>
			<Button v-if="running" variant="danger" size="m" @click="emit('cancel')">
				{{ t("contactResearchPage.actions.cancel") }}
			</Button>
		</div>
		<div class="run-progress" role="progressbar" :aria-valuenow="progress" aria-valuemin="0" aria-valuemax="100">
			<span :style="{ width: `${progress}%` }" />
		</div>
	</section>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import Button from "@/components/ui/Button.vue"
import { IconName } from "@/components/icons/registry"

defineProps<{
	running: boolean
	failed: boolean
	title: string
	subjectDetails: string
	duration: string
	completedProviderCount: number
	providerCount: number
	progress: number
}>()

const emit = defineEmits<{
	cancel: []
}>()

const { t } = useI18n()
</script>

<style scoped>
.panel {
	min-width: 0;
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	box-shadow: var(--shadow-sm);
}

.run-summary {
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto;
	align-items: center;
	gap: var(--space-16);
	padding: var(--space-16) var(--space-20);
	margin-bottom: var(--space-20);
}

.run-overview {
	display: flex;
	align-items: center;
	gap: var(--space-8);
}

.run-copy {
	strong,
	p {
		margin: 0;
	}

	p {
		margin-top: var(--space-4);
		font-size: var(--font-size-xs);
		color: var(--neutral-600);
	}
}

.run-metrics {
	display: flex;
	align-items: center;
	gap: var(--space-16);

	> div {
		display: grid;
		min-width: var(--space-64);
		text-align: right;

		span {
			font-size: var(--font-size-xs);
			text-transform: uppercase;
			color: var(--neutral-600);
		}
	}
}

.run-progress {
	grid-column: 1 / -1;
	height: var(--space-4);
	overflow: hidden;
	background: var(--neutral-200);
	border-radius: var(--border-radius-full);

	span {
		display: block;
		height: 100%;
		background: var(--primary-green);
		transition: width 0.2s ease;
	}
}

.status-icon {
	display: grid;
	place-items: center;
	color: var(--accent-success);

	&.failed {
		color: var(--accent-warning);
	}
}

.provider-status-icon {
	display: grid;
	place-items: center;
	width: var(--space-18);
	height: var(--space-18);
	background: transparent;
	border: 2px solid var(--neutral-200);
	border-right-color: var(--primary-green);
	border-radius: var(--border-radius-full);
	animation: spin 1s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

@media (max-width: 640px) {
	.run-summary {
		grid-template-columns: 1fr;
		align-items: flex-start;
	}

	.run-metrics {
		justify-content: space-between;
		flex-wrap: wrap;
	}

	.run-progress {
		grid-column: 1;
	}
}
</style>
